// src/components/TankbestellungModal.js
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import "./EmailPreviewModal.css"; // Wiederverwendung des vorhandenen Stils

function TankbestellungModal({ entries = [], formattedDate, onClose }) {
  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [emailTo, setEmailTo] = useState("brandwache.boardwalk@luerssen.de");
  const [emailCC, setEmailCC] = useState("m.walder@power-gruppe.de; mervyn.marcussen@luerssen.de");
  const textAreaRef = useRef(null);
  
  // Beim Laden den aktuellen Benutzer aus dem localStorage holen und E-Mail-Einstellungen aus Firebase laden
  useEffect(() => {
    const loadData = async () => {
      try {
        // Benutzer laden
        const userDataString = localStorage.getItem("currentUser");
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          setCurrentUser(userData);
        }

        // E-Mail-Einstellungen aus Firebase laden
        const settingsDoc = await getDoc(doc(db, 'settings', 'emailSettings'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          if (data.emailTo) setEmailTo(data.emailTo);
          if (data.emailCC) setEmailCC(data.emailCC);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Daten:", error);
      }
    };

    loadData();
  }, []);
  
  // Einfachen Plaintext für E-Mail erzeugen
  const generateEmailText = () => {
    // Kompakter Tabellenkopf
    const header = "ID|Datum|Tank|Firma";
    const divider = "--+-----+----+-----";
    
    // Tabellenzeilen erzeugen - maximale Kompaktheit
    const tableRows = entries.map(entry => {
      const id = String(entry.nummer || '-');
      const date = String(entry.datum || '');
      const tanks = Array.isArray(entry.tanks) ? entry.tanks.join(",") : entry.tanks || "-";
      const firma = entry.firma || '-';
      
      return `${id}|${date}|${tanks}|${firma}`;
    }).join('\n');
    
    // Benutzer für Signatur
    let userSignature = "Brandwache FDB Berne";
    if (currentUser && currentUser.username) {
      userSignature = `${currentUser.username}\nBrandwache FDB Berne`;
    }
    
    // Kompakter E-Mail-Text
    return `Sehr geehrte Damen und Herren,

Freimessung für ${formattedDate} anfordern:

${header}
${divider}
${tableRows}

Freimessungen: 03:00-06:00 Uhr
Bitte beschriften Sie die Tanks mit den entsprechenden Tankfreigabekarten.

Mit freundlichen Grüßen,
${userSignature}
Power-Brandschutz
Lürssen Werft
Industriestraße 6
27804 Berne`;
  };

  // E-Mail-Text für Anzeige (jetzt editierbar)
  const [emailText, setEmailText] = useState("");
  useEffect(() => {
    setEmailText(generateEmailText());
    // eslint-disable-next-line
  }, [entries, currentUser, formattedDate]);

  // E-Mail-Betreff
  const emailSubject = `Freimessungs-Bestellung für ${formattedDate} FDB Berne`;
  
  // Text in die Zwischenablage kopieren
  const handleCopyToClipboard = () => {
    try {
      if (textAreaRef.current) {
        textAreaRef.current.select();
        document.execCommand('copy');
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 3000);
      }
    } catch (err) {
      console.error("Kopier-Fehler:", err);
      setError("Fehler beim Kopieren in die Zwischenablage.");
    }
  };
  
  // Einfache, zuverlässige Methode um den Standard-E-Mail-Client zu öffnen
  const handleOpenEmail = () => {
    try {
      const mailtoLink = `mailto:${emailTo}?cc=${encodeURIComponent(emailCC)}&subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailText)}`;
      window.open(mailtoLink, '_blank');
    } catch (err) {
      console.error("E-Mail-Fehler:", err);
      setError("Beim Öffnen des E-Mail-Clients ist ein Fehler aufgetreten. Bitte nutzen Sie die Kopieren-Funktion.");
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: '95%', maxWidth: '1200px' }}>
        <h2>Vorschau: Freimessungs-Bestellung für {formattedDate}</h2>
        
        {error && <div className="error-message">{error}</div>}
        
        <p>Folgende Einträge werden in der Freimessungs-Bestellung enthalten sein:</p>
        
        <ul className="entries-list">
          {entries.map((entry) => {
            const tanks = Array.isArray(entry.tanks)
              ? entry.tanks.join(", ")
              : entry.tanks || "-";
              
            return (
              <li key={entry.id || entry.nummer}>
                <div className="entry-header">
                  <strong>Firma:</strong> {entry.firma || "Nicht angegeben"}
                </div>
                <div className="entry-details">
                  <span><strong>Tanks:</strong> {tanks}</span>
                  <span><strong>ID:</strong> {entry.nummer || "-"}</span>
                  <span><strong>Datum:</strong> {entry.datum || "-"}</span>
                </div>
              </li>
            );
          })}
        </ul>
        
        <div style={{ marginTop: '20px' }}>
          <p><strong>E-Mail-Empfänger:</strong> {emailTo}</p>
          <p><strong>CC:</strong> {emailCC}</p>
          <p><strong>Betreff:</strong> {emailSubject}</p>
        </div>
        
        <div style={{ marginTop: '10px', marginBottom: '20px' }}>
          <label htmlFor="email-text">
            <strong>E-Mail-Text (bearbeitbar):</strong>
            {isCopied && <span style={{ color: 'green', marginLeft: '10px' }}>✓ Kopiert!</span>}
          </label>
          <textarea
            ref={textAreaRef}
            id="email-text"
            value={emailText}
            onChange={e => setEmailText(e.target.value)}
            style={{
              width: '100%',
              height: '200px',
              marginTop: '5px',
              padding: '10px',
              fontFamily: 'monospace',
              resize: 'vertical',
              whiteSpace: 'pre',
              overflowX: 'auto'
            }}
          />
        </div>
        
        <div className="modal-buttons">
          <button onClick={onClose}>
            Abbrechen
          </button>
          <button 
            onClick={handleCopyToClipboard}
            style={{ backgroundColor: '#4CAF50' }}
          >
            In Zwischenablage kopieren
          </button>
          <button 
            onClick={handleOpenEmail}
            className="send-button"
          >
            E-Mail öffnen
          </button>
        </div>
      </div>
    </div>
  );
}

// PropTypes für bessere Typ-Sicherheit
TankbestellungModal.propTypes = {
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      nummer: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      datum: PropTypes.string.isRequired,
      tanks: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string)
      ]),
      firma: PropTypes.string
    })
  ),
  formattedDate: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

export default TankbestellungModal;